import { SafetyCertificateOutlined } from '@ant-design/icons'
import type { components } from '@fluencelabs/vodopad-admin-api'
import { useOne } from '@refinedev/core'
import {
  Breadcrumb,
  Button,
  Card,
  Descriptions,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { FormattedDate } from '@/components/common/FormattedDate'
import { MONO_STYLE, truncateMiddle } from '@/components/common/MonoId'
import { StatusTag } from '@/components/common/StatusTag'
import { UserEmailLink } from '@/components/common/UserEmailLink'
import { INTENT_STATUS_COLORS, PARK_REASON_LABELS } from '@/constants/payments'
import { formatUsdCents } from '@/utils/money'

import { ProviderMethod } from './components/ProviderMethod'
import { ResolveReviewModal } from './components/ResolveReviewModal'

type AdminIntentDetailDto = components['schemas']['AdminIntentDetailDto']
type ParsedEvidenceDto = components['schemas']['ParsedEvidenceDto']

const TRUST_COLORS: Record<string, string> = {
  provider_signed: 'green',
  admin: 'blue',
}

function evidenceColumns() {
  return [
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      width: 110,
    },
    {
      title: 'Provider payment ID',
      dataIndex: 'providerPaymentId',
      key: 'providerPaymentId',
      ellipsis: true,
      render: (v: string) => (
        <Typography.Text copyable style={MONO_STYLE}>
          {v}
        </Typography.Text>
      ),
    },
    {
      title: 'Claimed',
      dataIndex: 'claimedAmountCents',
      key: 'claimedAmountCents',
      width: 100,
      align: 'right' as const,
      render: (c: number) => formatUsdCents(c),
    },
    {
      title: 'Currency',
      dataIndex: 'paidCurrency',
      key: 'paidCurrency',
      width: 90,
    },
    {
      title: 'Normalized',
      dataIndex: 'normalizedStatus',
      key: 'normalizedStatus',
      width: 130,
      render: (s: string) => <StatusTag status={s} />,
    },
    {
      title: 'Trust',
      dataIndex: 'trustLevel',
      key: 'trustLevel',
      width: 130,
      render: (t: string) => (
        <Tag color={TRUST_COLORS[t] ?? 'default'}>{t}</Tag>
      ),
    },
    {
      title: 'Tx hashes',
      dataIndex: 'txHashes',
      key: 'txHashes',
      width: 200,
      render: (hashes: string[]) =>
        hashes.length === 0 ? (
          '—'
        ) : (
          <div>
            {hashes.map((h) => (
              <div key={h}>
                <Typography.Text copyable={{ text: h }} style={MONO_STYLE}>
                  {truncateMiddle(h)}
                </Typography.Text>
              </div>
            ))}
          </div>
        ),
    },
    {
      title: 'Parsed',
      dataIndex: 'parsedAt',
      key: 'parsedAt',
      width: 180,
      render: (d: string) => <FormattedDate date={d} fmt="PPp" />,
    },
  ]
}

function EvidenceDetail({ evidence }: { evidence: ParsedEvidenceDto }) {
  return (
    <Descriptions column={2} size="small" bordered>
      <Descriptions.Item label="Evidence ID">
        <Typography.Text copyable style={MONO_STYLE}>
          {evidence.id}
        </Typography.Text>
      </Descriptions.Item>
      <Descriptions.Item label="Raw record ID">
        <Typography.Text copyable style={MONO_STYLE}>
          {evidence.rawRecordId}
        </Typography.Text>
      </Descriptions.Item>
      <Descriptions.Item label="Intent ID">
        {evidence.intentId ? (
          <Typography.Text copyable style={MONO_STYLE}>
            {evidence.intentId}
          </Typography.Text>
        ) : (
          '—'
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Provider">
        {evidence.provider}
      </Descriptions.Item>
      <Descriptions.Item label="Provider payment ID">
        <Typography.Text copyable style={MONO_STYLE}>
          {evidence.providerPaymentId}
        </Typography.Text>
      </Descriptions.Item>
      <Descriptions.Item label="Claimed">
        {formatUsdCents(evidence.claimedAmountCents)}
      </Descriptions.Item>
      <Descriptions.Item label="Paid currency">
        {evidence.paidCurrency}
      </Descriptions.Item>
      <Descriptions.Item label="Normalized status">
        <StatusTag status={evidence.normalizedStatus} />
      </Descriptions.Item>
      <Descriptions.Item label="Trust level">
        <Tag color={TRUST_COLORS[evidence.trustLevel] ?? 'default'}>
          {evidence.trustLevel}
        </Tag>
      </Descriptions.Item>
      <Descriptions.Item label="Parsed at">
        <FormattedDate date={evidence.parsedAt} fmt="PPpp" />
      </Descriptions.Item>
      <Descriptions.Item label="Tx hashes" span={2}>
        {evidence.txHashes.length === 0
          ? '—'
          : evidence.txHashes.map((h) => (
              <div key={h}>
                <Typography.Text copyable style={MONO_STYLE}>
                  {h}
                </Typography.Text>
              </div>
            ))}
      </Descriptions.Item>
    </Descriptions>
  )
}

export default function PaymentShowPage() {
  const { id: intentId } = useParams<{ id: string }>()
  const [resolveOpen, setResolveOpen] = useState(false)

  const { result: intent, query } = useOne<AdminIntentDetailDto>({
    resource: 'payments',
    id: intentId,
    queryOptions: { enabled: Boolean(intentId) },
  })

  if (!intentId) return null

  if (query.isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!intent) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        Payment intent not found
      </div>
    )
  }

  const isParked = intent.status === 'needs_review'

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/payments">Payments</Link> },
          { title: intent.intentId },
        ]}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <h2 style={{ margin: 0 }}>
          Payment intent{' '}
          <StatusTag status={intent.status} colorMap={INTENT_STATUS_COLORS} />
        </h2>
        {isParked && (
          <Button
            type="primary"
            icon={<SafetyCertificateOutlined />}
            onClick={() => setResolveOpen(true)}
          >
            Resolve review
          </Button>
        )}
      </div>

      <Card style={{ marginBottom: 24 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Intent ID">
            <Typography.Text copyable style={MONO_STYLE}>
              {intent.intentId}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="User">
            <UserEmailLink userId={intent.userId} />
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <StatusTag status={intent.status} colorMap={INTENT_STATUS_COLORS} />
          </Descriptions.Item>
          <Descriptions.Item label="Provider">
            <ProviderMethod
              provider={intent.provider}
              methodCode={intent.methodCode}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Requested">
            {formatUsdCents(intent.requestedAmountCents)}
          </Descriptions.Item>
          <Descriptions.Item label="Claimed">
            {formatUsdCents(intent.claimedAmountCents)}
          </Descriptions.Item>
          <Descriptions.Item label="Park reason">
            {intent.parkReason ? (
              <Tag color="warning">{PARK_REASON_LABELS[intent.parkReason]}</Tag>
            ) : (
              '—'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Created">
            <FormattedDate date={intent.createdAt} fmt="PPp" />
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={`Evidence (${intent.evidence.length})`}>
        <Table<ParsedEvidenceDto>
          dataSource={intent.evidence}
          rowKey="id"
          columns={evidenceColumns()}
          pagination={false}
          size="small"
          scroll={{ x: 900 }}
          locale={{ emptyText: 'No evidence linked to this intent' }}
          expandable={{
            defaultExpandAllRows: true,
            expandedRowRender: (e) => <EvidenceDetail evidence={e} />,
          }}
        />
      </Card>

      {isParked && (
        <ResolveReviewModal
          open={resolveOpen}
          intentId={intent.intentId}
          userId={intent.userId}
          claimedAmountCents={intent.claimedAmountCents}
          onClose={() => setResolveOpen(false)}
        />
      )}
    </div>
  )
}
